import AnnouncementIcon from '@mui/icons-material/Announcement';
import CloseIcon from '@mui/icons-material/Close';
import EventIcon from '@mui/icons-material/Event';
import KeyboardDoubleArrowUp from '@mui/icons-material/KeyboardDoubleArrowUp';
import SearchIcon from '@mui/icons-material/Search';
import IconButton from "@mui/material/IconButton";
import InputAdornment from "@mui/material/InputAdornment";
import TextField from "@mui/material/TextField";
import Tooltip from "@mui/material/Tooltip";
import { styled } from "@mui/material";
import _ from 'lodash';
import { useEffect, useMemo, useState } from 'react';
import { KeyboardKey } from '_shared/_utilities/keyboard-keys';
import { MarkReadIcon } from "_shared/_views/buttons/read-state-icons";

/**
 * The search field, with Jump to oldest unread and Mark all read beside it. A Correspondence pane draws
 * it as a fixed header over its list, outside the part that scrolls (#5839).
 */
const SearchRow = styled("div")(({ theme }) => ({
  alignItems: "center",
  boxSizing: "border-box",
  display: "flex",
  gap: theme.spacing(0.5),
  width: '100%',
}));
const SearchField = styled(TextField)(({ theme }) => ({
  // Takes what the row has left once Mark all read is seated beside it.
  flex: "1 1 auto",
  minWidth: 0,
  // White on the header's light blue, as the controls beside it are: they read as things to press.
  "& .MuiOutlinedInput-root": {
    backgroundColor: theme.palette.background.paper,
    // The buttons' 40px. At 14px the field's own padding made it 37px, a step short of them.
    height: 40,
  },
  // The messages' own 14px, so a search reads at the size of what it finds.
  "& .MuiInputBase-input": {
    fontSize: "0.875rem",
  },
}));
/**
 * A control of its own beside the field, not inside it: bordered like the field, the same height, and
 * white like it on the header's light blue.
 */
const RowControl = styled(IconButton)(({ theme }) => ({
  backgroundColor: theme.palette.background.paper,
  border: `1px solid ${theme.palette.divider}`,
  borderRadius: theme.shape.borderRadius,
  flexShrink: 0,
  height: 40,
  width: 40,
}));

type SearchBarProps = {
  className?: string;
  showSystemMessages?: boolean;
  filterDates: boolean;
  /** Disables the date toggle: a conversation with nothing in it has no dates to filter by. */
  datesDisabled?: boolean;
  onToggleDates: () => void;
  /**
   * Offers the system-messages filter. A conversation with no system messages leaves it out: a proposal's
   * comments are all written by people.
   */
  onToggleSystemMessages?: () => void;
  onSearch: (searchString: string) => void;
  /** Offers Mark all read, at the top of the conversation. */
  onMarkAllRead?: () => void;
  /** Nothing is unread. */
  markAllReadDisabled?: boolean;
  /**
   * Offers Jump to oldest unread, beside Mark all read. It brings the oldest unread message on screen,
   * loading older pages until it arrives, however far back it is (#5828).
   */
  onJumpToOldestUnread?: () => void;
  /** Nothing is unread, or the jump is still loading. */
  jumpToOldestUnreadDisabled?: boolean;
};

export default function SearchBar(props: Readonly<SearchBarProps>) {
  const {
    className,
    filterDates,
    datesDisabled,
    onToggleDates,
    onToggleSystemMessages,
    onSearch,
    onMarkAllRead,
    markAllReadDisabled,
    onJumpToOldestUnread,
    jumpToOldestUnreadDisabled,
  } = props;
  const showSystemMessages = props.showSystemMessages ?? false;

  const [searchString, setSearchString] = useState<string>('');

  const debouncedSearch = useMemo(() => _.debounce((value: string) => onSearch(value), 500), [onSearch]);

  useEffect(() => {
    return () => debouncedSearch.cancel();
  }, [debouncedSearch]);

  function handleSearchStringChanged(event: React.ChangeEvent<HTMLInputElement>) {
    const value = event.target.value;
    setSearchString(value);
    debouncedSearch(value);
  }

  function clearSearch() {
    debouncedSearch.cancel();
    setSearchString('');
    onSearch('');
  }

  return (
    <SearchRow className={className} data-testid="message-search-row">
    <SearchField
      variant="outlined"
      placeholder="Search"
      size="small"
      fullWidth={true}
      onChange={handleSearchStringChanged}
      onKeyDown={(event) => {
        if (event.key === KeyboardKey.Enter) {
          onSearch(searchString);
        }
        if (event.key === KeyboardKey.Escape) {
          clearSearch();
        }
      }}
      value={searchString}
      slotProps={{
        input: {
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon color='disabled' />
            </InputAdornment>
          ),
          endAdornment: (
            <>
              {searchString && (
                <Tooltip
                  arrow
                  title="Clear Search"
                >
                  <IconButton
                    size="small"
                    onClick={clearSearch}>
                    <CloseIcon />
                  </IconButton>
                </Tooltip>
              )}
              {onToggleSystemMessages && (
                <Tooltip
                  arrow
                  title={
                    showSystemMessages ? "Show All Messages" : "Only Show System Messages"
                  }
                >
                  <IconButton
                    size="small"
                    onClick={onToggleSystemMessages}>
                    <AnnouncementIcon
                      color={showSystemMessages ? 'primary' : 'inherit'}
                    />
                  </IconButton>
                </Tooltip>
              )}
              <Tooltip
                arrow
                describeChild
                title={
                  filterDates ? "Stop Filtering By Date" : "Filter By Date"
                }
              >
                {/* The span keeps the tooltip working over a disabled button; the button keeps the name. */}
                <span>
                <IconButton
                  aria-label={filterDates ? "Stop Filtering By Date" : "Filter By Date"}
                  disabled={datesDisabled}
                  size="small"
                  onClick={onToggleDates}>
                  <EventIcon 
                    color={filterDates ? 'primary' : 'inherit'}
                  />
                </IconButton>
                </span>
              </Tooltip>
            </>
          )
        }
      }}
    />
    {onJumpToOldestUnread && (
      <Tooltip arrow describeChild title="Jump to oldest unread">
        {/* The span keeps the tooltip working over a disabled button; the button keeps the name. */}
        <span>
          <RowControl
            aria-label="Jump to oldest unread"
            color="primary"
            disabled={jumpToOldestUnreadDisabled}
            onClick={onJumpToOldestUnread}>
            <KeyboardDoubleArrowUp fontSize="small" />
          </RowControl>
        </span>
      </Tooltip>
    )}
    {onMarkAllRead && (
      <Tooltip arrow describeChild title="Mark all read">
        {/* The span keeps the tooltip working over a disabled button; the button keeps the name. */}
        <span>
          <RowControl
            aria-label="Mark all read"
            color="primary"
            disabled={markAllReadDisabled}
            onClick={onMarkAllRead}>
            <MarkReadIcon fontSize="small" />
          </RowControl>
        </span>
      </Tooltip>
    )}
    </SearchRow>
  )
}
